// ============================================================================
// Feedback for "place an AI call" actions.
// ============================================================================
// POST /crm/calls/place (and /place-company) answers 201 even when the provider
// REFUSED the call: the orchestrator already stored the attempt as a `failed` /
// `skipped` crm_calls document, and returning it (instead of a bare 500) is what
// lets the UI show the reason. The flip side is that `await placeAiCall(...)`
// no longer throws for those calls, so every call site MUST inspect the returned
// record before congratulating the user — otherwise it shows a green "llamando…"
// toast for a call that was never placed.
//
// This helper is that check, shared by every caller (Llamadas, the lead "Llamar
// con IA" button and the workflow test modal).
// ============================================================================

import Swal from "sweetalert2";
import { brand } from "../../../config/theme";
import type { Call } from "./callsService";

/** Statuses that mean the call is over before it began. */
const TERMINAL_FAILURE: Call["status"][] = ["failed", "skipped"];

/** A call record as returned by placeAiCall / placeCompanyAiCall. */
type PlacedCall = Pick<Call, "status" | "statusDetail"> | null | undefined;

/** True when the provider refused the call (no transcript will ever arrive). */
export function isCallProblem(call: PlacedCall): boolean {
  return Boolean(call && TERMINAL_FAILURE.includes(call.status));
}

/**
 * Show a warning when the call could not be placed.
 * @returns true when it announced a problem (the caller must then NOT show its
 *          own success message).
 */
export function announceCallProblem(call: PlacedCall): boolean {
  if (!isCallProblem(call)) return false;
  Swal.fire({
    icon: "warning",
    title: call!.status === "skipped" ? "Llamada omitida" : "La llamada no se pudo colocar",
    text: call!.statusDetail
      ? `Motivo: ${call!.statusDetail}`
      : "Quedó registrada en el Historial. Revisa la configuración del proveedor de voz.",
    confirmButtonColor: brand.primary,
  });
  return true;
}
